import { ref, computed, type Ref, type ComputedRef } from 'vue';
import type { Orders_EntregasItemRow } from '../contracts/Orders_EntregasItemRow';

/**
 * Client-side KPI filtering for the /pdv/entregas page.
 *
 * The five KPI counts come from GET /so/entregas/kpis (So_entregas_kpi_service on
 * the backend), while the table rows come from GET /so/entregas/items. Clicking a
 * KPI filters the already-loaded rows, so each predicate below is the row-level
 * mirror of the corresponding SQL count:
 *
 *   today    -> countTodayAndNext2Days: expected_delivery_at in [today, today+3)
 *   delayed  -> countDelayed:           expected_delivery_at < today
 *   alerts   -> countPendingAlerts:     PRONTO with no pickup notification yet
 *   pending  -> countPendingDelivery:   PRONTO services + undelivered products
 *
 * `rescheduled` (countRescheduled) has NO filterable counterpart: it counts items
 * with a so_reprocessos row, and the items row contract carries no reprocesso
 * field — so that card stays non-clickable.
 *
 * The counts and the filtered row set can differ slightly: the KPI SQL counts
 * every undelivered item, while the items list also excludes items still blocked
 * by incomplete work-order steps.
 */
export type EntregasKpiKey = 'today' | 'delayed' | 'alerts' | 'pending-delivery';

const ENTREGAS_KPI_PREDICATES: Record<EntregasKpiKey, (row: Orders_EntregasItemRow) => boolean> = {
  today: (row) => row.days_until_due != null && row.days_until_due >= 0 && row.days_until_due <= 2,
  delayed: (row) => row.days_until_due != null && row.days_until_due < 0,
  alerts: (row) => row.item_status === 'pronto' && (row.notification_count ?? 0) === 0,
  'pending-delivery': (row) => row.item_status === 'pronto' || row.item_status === 'disponivel',
};

export function useEntregasKpiFilter(
  rows: Ref<Orders_EntregasItemRow[]> | ComputedRef<Orders_EntregasItemRow[]>,
) {
  const activeKpi = ref<EntregasKpiKey | null>(null);

  /** Toggle: clicking the active KPI clears the filter, another one replaces it. */
  function toggleKpi(key: EntregasKpiKey): void {
    activeKpi.value = activeKpi.value === key ? null : key;
  }

  const filteredRows = computed<Orders_EntregasItemRow[]>(() => {
    const key = activeKpi.value;
    if (!key) return rows.value;
    return rows.value.filter(ENTREGAS_KPI_PREDICATES[key]);
  });

  return { activeKpi, toggleKpi, filteredRows };
}
